import { useEffect, useState, useRef } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import styles from './AccountPage.module.css'

interface EditFormData {
  username?: string
  email?: string
}

interface FormErrors {
  username?: string
  email?: string
}

export function AccountPage() {
  const {
    user,
    fullProfile,
    token,
    initialized,
    loading,
    logout,
    fetchFullProfile,
    updateProfile,
    deleteAccount,
  } = useAuth()
  const navigate = useNavigate()

  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState<EditFormData>({})
  const [editErrors, setEditErrors] = useState<FormErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const usernameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)

  // Redirect si pas d'auth – attendre l'initialisation du contexte
  useEffect(() => {
    if (initialized && !token) {
      navigate('/login')
    }
    // Note : token peut être null pendant l'initialisation;
    // on ne redirige que lorsque le contexte est prêt.
  }, [initialized, token, navigate])

  // Charger le profil complet au mount
  useEffect(() => {
    if (token && !fullProfile) {
      fetchFullProfile().catch(() => {
        // Le profil peut ne pas être disponible immédiatement
      })
    }
  }, [token, fullProfile, fetchFullProfile])

  // Initialiser les données d'édition
  useEffect(() => {
    if (fullProfile && isEditing && !editData.username) {
      setEditData({
        username: fullProfile.username || '',
        email: fullProfile.email || '',
      })
    }
  }, [isEditing, fullProfile, editData.username])

  // afficher un loader tant que l'information d'utilisateur n'est pas prête
  if (!initialized) {
    return (
      <main className={styles.page}>
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner} aria-label="Chargement" />
        </div>
      </main>
    )
  }

  // si après initialisation il n'y a ni user ni fullProfile, on redirige
  if (!user && !fullProfile) {
    return null
  }

  const validateEdit = (data: EditFormData): FormErrors => {
    const errors: FormErrors = {}
    if (!data.username?.trim()) {
      errors.username = 'Le nom d\'utilisateur est requis.'
    }
    if (!data.email?.trim()) {
      errors.email = 'L\'email est requis.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'L\'email n\'est pas valide.'
    }
    return errors
  }

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target
    setEditData((prev) => ({ ...prev, [name]: value }))
    if (editErrors[name as keyof FormErrors]) {
      setEditErrors((prev) => ({ ...prev, [name]: undefined }))
    }
    if (apiError) setApiError(null)
  }

  const handleEditCancel = () => {
    setIsEditing(false)
    setEditData({})
    setEditErrors({})
    setApiError(null)
  }

  const handleEditSave = async () => {
    const errors = validateEdit(editData)
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors)
      if (errors.username) usernameRef.current?.focus()
      else if (errors.email) emailRef.current?.focus()
      return
    }

    try {
      // Construire les updates mappés au Pascal case pour ASP.NET
      const updates: Record<string, any> = {}
      if (editData.username) updates.UserName = editData.username
      if (editData.email) updates.Email = editData.email

      await updateProfile(updates)
      setIsEditing(false)
      setApiError(null)
    } catch (err: any) {
      setApiError(err.message || 'Erreur lors de la mise à jour.')
    }
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true)
    try {
      await deleteAccount()
      // deleteAccount appelle logout automatiquement
      navigate('/')
    } catch (err: any) {
      setApiError(err.message || 'Erreur lors de la suppression.')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Mon Compte</h1>
        <p className={styles.subtitle}>
          Gérez vos informations personnelles et vos paramètres
        </p>
      </div>

      <div className={styles.card}>
        {/* Section d'affichage du profil */}
        {!isEditing ? (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Informations Personnelles</h2>

            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Email</span>
              <span className={styles.infoValue}>
                {fullProfile?.email ?? user?.email ?? ''}
              </span>
            </div>

            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Nom d'utilisateur</span>
              <span className={styles.infoValue}>
                {fullProfile?.username ?? user?.username ?? ''}
              </span>
            </div>

            {fullProfile?.createdAt && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Créé le</span>
                <span className={styles.infoValue}>
                  {new Date(fullProfile.createdAt).toLocaleDateString(
                    'fr-FR'
                  )}
                </span>
              </div>
            )}

            {fullProfile?.lastLogin && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Dernière visite</span>
                <span className={styles.infoValue}>
                  {new Date(fullProfile.lastLogin).toLocaleDateString(
                    'fr-FR'
                  )}
                </span>
              </div>
            )}

            {apiError && (
              <div className={styles.apiError}>{apiError}</div>
            )}

            <div className={styles.buttonGroup}>
              <button
                className={styles.btn + ' ' + styles.btnDanger}
                onClick={() => setShowDeleteModal(true)}
              >
                Supprimer mon compte
              </button>
              <button
                className={styles.btn + ' ' + styles.btnSecondary}
                onClick={() => logout()}
              >
                Se déconnecter
              </button>
              <button
                className={styles.btn + ' ' + styles.btnPrimary}
                onClick={() => setIsEditing(true)}
                disabled={loading}
              >
                Modifier
              </button>
            </div>
          </div>
        ) : (
          /* Formulaire d'édition */
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Modifier mes informations</h2>

            <form className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="username" className={styles.label}>
                  Nom d'utilisateur
                </label>
                <input
                  ref={usernameRef}
                  id="username"
                  name="username"
                  type="text"
                  className={styles.input}
                  value={editData.username || ''}
                  onChange={handleEditChange}
                  disabled={loading}
                />
                {editErrors.username && (
                  <div className={styles.errorMsg}>{editErrors.username}</div>
                )}
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>
                  Email
                </label>
                <input
                  ref={emailRef}
                  id="email"
                  name="email"
                  type="email"
                  className={styles.input}
                  value={editData.email || ''}
                  onChange={handleEditChange}
                  disabled={loading}
                />
                {editErrors.email && (
                  <div className={styles.errorMsg}>{editErrors.email}</div>
                )}
              </div>

              {apiError && (
                <div className={styles.apiError}>{apiError}</div>
              )}

              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={styles.btn + ' ' + styles.btnSecondary}
                  onClick={handleEditCancel}
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className={styles.btn + ' ' + styles.btnPrimary}
                  onClick={handleEditSave}
                  disabled={loading}
                >
                  {loading ? '...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Modal de suppression */}
      {showDeleteModal && (
        <div
          className={styles.modal}
          onClick={() => !isDeleting && setShowDeleteModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={styles.modalTitle}>Supprimer mon compte</h3>
            <p className={styles.modalMessage}>
              Cette action est irréversible. Votre compte et tous vos articles
              seront supprimés définitivement.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.btn + ' ' + styles.btnSecondary}
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                className={styles.btn + ' ' + styles.btnDanger}
                onClick={handleDeleteAccount}
                disabled={isDeleting}
              >
                {isDeleting ? '...' : 'Confirmer la suppression'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
